import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { format, differenceInDays, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, parseISO, isValid } from 'date-fns';
import Icon from '../../../components/AppIcon';
import ResourceDetailModal from './ResourceDetailModal';


// ─── Column definitions ───────────────────────────────────────────────────────
const ALL_COLUMNS = [
  { id: 'name',        label: 'Resource',    defaultWidth: 180, required: true },
  { id: 'category',    label: 'Category',    defaultWidth: 110 },
  { id: 'status',      label: 'Status',      defaultWidth: 90 },
  { id: 'assignments', label: 'Assignments', defaultWidth: 80 },
];

const STATUS_COLORS = {
  Assigned:    'bg-blue-100 text-blue-700',
  Available:   'bg-green-100 text-green-700',
  Maintenance: 'bg-amber-100 text-amber-700',
};

const BAR_COLORS = {
  current:  'bg-blue-500 text-white',
  upcoming: 'bg-purple-500 text-white',
  past:     'bg-gray-400 text-white',
};

// ─── Resizable column hook ────────────────────────────────────────────────────
function useResizableColumns(initialCols) {
  const [widths, setWidths] = useState(() =>
    Object.fromEntries(initialCols?.map(c => [c?.id, c?.defaultWidth]))
  );
  const dragging = useRef(null);

  const onMouseDown = useCallback((colId, e) => {
    e?.preventDefault();
    dragging.current = { colId, startX: e?.clientX, startW: widths?.[colId] };
    const onMove = (ev) => {
      if (!dragging?.current) return;
      const delta = ev?.clientX - dragging?.current?.startX;
      setWidths(prev => ({
        ...prev,
        [dragging?.current?.colId]: Math.max(60, dragging?.current?.startW + delta),
      }));
    };
    const onUp = () => {
      dragging.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [widths]);

  return { widths, setWidths, onMouseDown };
}

// ─── Assignments panel ────────────────────────────────────────────────────────
const AssignmentsPanel = ({ resource, onClose }) => {
  if (!resource) return null;
  const assignments = resource?.assignments || [];
  return (
    <div className="border-t border-border bg-muted/30">
      <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/50">
        <div className="flex items-center gap-2">
          <Icon name="CalendarDays" size={14} className="text-accent" />
          <span className="text-sm font-semibold text-foreground">{resource?.name} — Assignments</span>
          <span className="text-xs bg-accent/10 text-accent px-2 py-0.5 rounded-full">{assignments?.length}</span>
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded">
          <Icon name="X" size={14} />
        </button>
      </div>
      {assignments?.length === 0 ? (
        <div className="px-4 py-4 text-sm text-muted-foreground text-center">No assignments found.</div>
      ) : (
        <div className="divide-y divide-border">
          {assignments?.map((a, i) => {
            const start = a?.startDate ? format(new Date(a.startDate), 'MMM d, yyyy') : '—';
            const end   = a?.endDate   ? format(new Date(a.endDate),   'MMM d, yyyy') : '—';
            const color = a?.isCurrent ? BAR_COLORS?.current : a?.isUpcoming ? BAR_COLORS?.upcoming : BAR_COLORS?.past;
            return (
              <div key={a?.id || i} className="flex items-center gap-3 px-4 py-2 hover:bg-muted/40 transition-colors">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${color?.split(' ')?.[0]}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-foreground truncate">{a?.projectName || 'Project'}</p>
                  {a?.phaseName && <p className="text-xs text-muted-foreground truncate">{a?.phaseName}</p>}
                </div>
                <div className="text-xs text-muted-foreground whitespace-nowrap">{start} – {end}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Column Picker ────────────────────────────────────────────────────────────
const ColumnPicker = ({ columns, visible, onChange, onClose }) => {
  return (
    <div className="absolute right-0 top-full mt-1 z-50 bg-card border border-border rounded-lg shadow-lg p-3 w-48">
      <p className="text-xs font-semibold text-foreground mb-2">Show Columns</p>
      {columns?.map(col => (
        <label key={col?.id} className="flex items-center gap-2 py-1 cursor-pointer">
          <input
            type="checkbox"
            checked={visible?.includes(col?.id)}
            disabled={col?.required}
            onChange={() => onChange(col?.id)}
            className="rounded border-border text-accent"
          />
          <span className="text-xs text-foreground">{col?.label}</span>
          {col?.required && <span className="text-xs text-muted-foreground ml-auto">(required)</span>}
        </label>
      ))}
      <button onClick={onClose} className="mt-2 w-full text-xs text-center text-muted-foreground hover:text-foreground">Close</button>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const ResourceGanttView = ({ resources, filters }) => {
  const [pixelsPerDay, setPixelsPerDay] = useState(32);
  const [visibleCols, setVisibleCols] = useState(ALL_COLUMNS?.map(c => c?.id));
  const [showColPicker, setShowColPicker] = useState(false);
  const [expandedResource, setExpandedResource] = useState(null);
  const [detailResource, setDetailResource] = useState(null);

  // Date range state
  const [dateRangeMode, setDateRangeMode] = useState('auto'); // 'auto' | 'custom'
  const [customStart, setCustomStart] = useState(() => format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [customEnd,   setCustomEnd]   = useState(() => format(endOfMonth(addMonths(new Date(), 2)), 'yyyy-MM-dd'));

  const { widths, onMouseDown } = useResizableColumns(ALL_COLUMNS);

  // Filter resources
  const filteredResources = useMemo(() => {
    const today = new Date();
    today?.setHours(0, 0, 0, 0);
    return (resources || [])?.filter(resource => {
      if (filters?.search) {
        const s = filters?.search?.toLowerCase();
        if (!resource?.name?.toLowerCase()?.includes(s) && !resource?.identifier?.toLowerCase()?.includes(s)) return false;
      }
      if (filters?.category && filters?.category !== 'all') {
        if (resource?.categoryId !== filters?.category) return false;
      }
      if (filters?.status && filters?.status !== 'all') {
        if (filters?.status === 'conflicts') {
          if (!resource?.conflicts?.length) return false;
        } else {
          const map = { 'available': 'Available', 'in-use': 'In Use', 'maintenance': 'Maintenance', 'unavailable': 'Unavailable' };
          if (resource?.status !== (map?.[filters?.status] || filters?.status)) return false;
        }
      }
      const usageTimeline = filters?.usageTimeline;
      if (usageTimeline && usageTimeline !== 'all') {
        const assignments = resource?.assignments || [];
        const has = assignments?.some(a => {
          if (!a?.startDate || !a?.endDate) return false;
          const start = new Date(a.startDate); start?.setHours(0,0,0,0);
          const end   = new Date(a.endDate);   end?.setHours(23,59,59,999);
          const daysUntilStart = differenceInDays(start, today);
          const daysUntilEnd   = differenceInDays(end, today);
          if (daysUntilEnd < -30) return false;
          if (usageTimeline === 'within-30') return daysUntilStart <= 30 && daysUntilEnd >= -30;
          if (usageTimeline === 'within-60') return daysUntilStart <= 60 && daysUntilEnd >= -30;
          if (usageTimeline === 'within-90') return daysUntilStart <= 90 && daysUntilEnd >= -30;
          if (usageTimeline === 'beyond-90') return daysUntilStart > 90;
          return true;
        });
        if (!has) return false;
      }
      return true;
    });
  }, [resources, filters]);

  // Compute date range
  const { minDate, maxDate, days } = useMemo(() => {
    if (dateRangeMode === 'custom') {
      const min = parseISO(customStart);
      const max = parseISO(customEnd);
      if (isValid(min) && isValid(max) && min <= max) {
        return { minDate: min, maxDate: max, days: eachDayOfInterval({ start: min, end: max }) };
      }
    }
    // auto
    const allDates = filteredResources?.flatMap(r => (r?.assignments || [])?.flatMap(a => {
      if (a?.startDate && a?.endDate) return [new Date(a.startDate), new Date(a.endDate)];
      return [];
    }))?.filter(d => d instanceof Date && !isNaN(d));

    if (!allDates?.length) {
      const min = startOfMonth(new Date());
      const max = endOfMonth(addMonths(new Date(), 2));
      return { minDate: min, maxDate: max, days: eachDayOfInterval({ start: min, end: max }) };
    }
    const min = startOfMonth(new Date(Math.min(...allDates.map(d => d.getTime()))));
    const max = endOfMonth(new Date(Math.max(...allDates.map(d => d.getTime()))));
    return { minDate: min, maxDate: max, days: eachDayOfInterval({ start: min, end: max }) };
  }, [filteredResources, dateRangeMode, customStart, customEnd]);

  // Month groups
  const monthGroups = useMemo(() => {
    const groups = [];
    let cur = null, count = 0;
    days?.forEach(day => {
      const key = format(day, 'MMM yyyy');
      if (key !== cur) { if (cur) groups?.push({ label: cur, count }); cur = key; count = 1; }
      else count++;
    });
    if (cur) groups?.push({ label: cur, count });
    return groups;
  }, [days]);

  const totalLeftWidth = useMemo(() =>
    ALL_COLUMNS?.filter(c => visibleCols?.includes(c?.id))?.reduce((s, c) => s + widths?.[c?.id], 0)
  , [visibleCols, widths]);

  const getBarStyle = (assignment) => {
    if (!assignment?.startDate || !assignment?.endDate) return null;
    const startDiff = differenceInDays(new Date(assignment.startDate), minDate);
    const duration  = Math.max(1, differenceInDays(new Date(assignment.endDate), new Date(assignment.startDate)) + 1);
    return { left: `${startDiff * pixelsPerDay}px`, width: `${duration * pixelsPerDay}px` };
  };

  const getBarColor = (a) => a?.isCurrent ? BAR_COLORS?.current : a?.isUpcoming ? BAR_COLORS?.upcoming : BAR_COLORS?.past;

  const toggleColumn = (id) => {
    setVisibleCols(prev =>
      prev?.includes(id) ? prev?.filter(c => c !== id) : [...prev, id]
    );
  };

  const handleResourceClick = (resource) => {
    setExpandedResource(prev => prev?.id === resource?.id ? null : resource);
  };

  const handleResourceDoubleClick = (resource) => {
    setDetailResource(resource);
  };

  if (filteredResources?.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-10 text-center">
        <Icon name="BarChart3" size={40} className="mx-auto mb-3 text-muted-foreground" />
        <h3 className="text-base font-semibold text-foreground mb-1">No Resources to Display</h3>
        <p className="text-sm text-muted-foreground">
          {filters?.search || filters?.category !== 'all' || filters?.status !== 'all' ?'No resources match your current filters.' :'Add resources to see them in the Gantt chart view.'}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* ── Controls bar ── */}
      <div className="px-3 py-2 border-b border-border flex items-center justify-between flex-wrap gap-2">
        {/* Left: resource count */}
        <div className="flex items-center gap-1.5">
          <Icon name="Info" size={14} className="text-muted-foreground" />
          <span className="text-xs text-muted-foreground">
            {filteredResources?.length} resource{filteredResources?.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Right: date range + zoom + column picker */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Date range */}
          <div className="flex items-center gap-1.5 border border-border rounded px-2 py-1 bg-muted/30">
            <Icon name="CalendarRange" size={13} className="text-muted-foreground" />
            <select
              value={dateRangeMode}
              onChange={e => setDateRangeMode(e?.target?.value)}
              className="text-xs bg-transparent text-foreground border-none outline-none cursor-pointer"
            >
              <option value="auto">Auto Range</option>
              <option value="custom">Custom Range</option>
            </select>
            {dateRangeMode === 'custom' && (
              <>
                <input
                  type="date"
                  value={customStart}
                  onChange={e => setCustomStart(e?.target?.value)}
                  className="text-xs bg-transparent text-foreground border-none outline-none cursor-pointer"
                />
                <span className="text-xs text-muted-foreground">–</span>
                <input
                  type="date"
                  value={customEnd}
                  onChange={e => setCustomEnd(e?.target?.value)}
                  className="text-xs bg-transparent text-foreground border-none outline-none cursor-pointer"
                />
              </>
            )}
          </div>

          {/* Zoom */}
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground">Zoom:</span>
            <button
              onClick={() => setPixelsPerDay(p => Math.max(p - 8, 16))}
              disabled={pixelsPerDay <= 16}
              className="p-1 rounded border border-border hover:bg-muted disabled:opacity-40 transition-colors"
            >
              <Icon name="ZoomOut" size={13} />
            </button>
            <span className="text-xs text-muted-foreground w-10 text-center">{pixelsPerDay}px</span>
            <button
              onClick={() => setPixelsPerDay(p => Math.min(p + 8, 80))}
              disabled={pixelsPerDay >= 80}
              className="p-1 rounded border border-border hover:bg-muted disabled:opacity-40 transition-colors"
            >
              <Icon name="ZoomIn" size={13} />
            </button>
          </div>

          {/* Column picker */}
          <div className="relative">
            <button
              onClick={() => setShowColPicker(p => !p)}
              className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-border hover:bg-muted transition-colors"
            >
              <Icon name="Columns" size={13} />
              Columns
            </button>
            {showColPicker && (
              <ColumnPicker
                columns={ALL_COLUMNS}
                visible={visibleCols}
                onChange={toggleColumn}
                onClose={() => setShowColPicker(false)}
              />
            )}
          </div>
        </div>
      </div>
      {/* ── Legend ── */}
      <div className="px-3 py-1.5 border-b border-border flex items-center gap-3">
        <span className="text-xs text-muted-foreground">Legend:</span>
        {[['bg-blue-500','Current'],['bg-purple-500','Upcoming'],['bg-gray-400','Past'],['bg-red-500','Conflict']]?.map(([bg, label]) => (
          <div key={label} className="flex items-center gap-1">
            <div className={`w-2.5 h-2.5 rounded ${label === 'Conflict' ? 'rounded-full' : ''} ${bg}`} />
            <span className="text-xs text-muted-foreground">{label}</span>
          </div>
        ))}
      </div>
      {/* ── Scrollable chart ── */}
      <div className="overflow-x-auto">
        <div style={{ minWidth: `${totalLeftWidth + days?.length * pixelsPerDay}px` }}>

          {/* Month header */}
          <div className="flex border-b border-border bg-muted sticky top-0 z-20">
            {/* Left columns header */}
            <div className="flex flex-shrink-0 sticky left-0 z-30 bg-muted" style={{ width: totalLeftWidth }}>
              {ALL_COLUMNS?.filter(c => visibleCols?.includes(c?.id))?.map((col, i, arr) => (
                <div
                  key={col?.id}
                  className="relative flex items-center px-2 py-1.5 border-r border-border font-semibold text-xs text-foreground select-none"
                  style={{ width: widths?.[col?.id] }}
                >
                  <span className="truncate">{col?.label}</span>
                  {/* Resize handle */}
                  <div
                    onMouseDown={e => onMouseDown(col?.id, e)}
                    className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-accent/40 transition-colors"
                  />
                </div>
              ))}
            </div>
            {/* Month spans */}
            <div className="flex">
              {monthGroups?.map((g, i) => (
                <div
                  key={i}
                  className="border-r border-border px-2 py-1.5 text-xs font-semibold text-foreground bg-muted/80 flex items-center"
                  style={{ width: `${g?.count * pixelsPerDay}px` }}
                >
                  {g?.label}
                </div>
              ))}
            </div>
          </div>

          {/* Day header */}
          <div className="flex border-b border-border bg-muted/40">
            <div className="flex-shrink-0 border-r border-border sticky left-0 z-20 bg-muted/40" style={{ width: totalLeftWidth }} />
            <div className="flex">
              {days?.map((day, idx) => {
                const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                return (
                  <div
                    key={idx}
                    className={`flex-shrink-0 border-r border-border/40 text-center py-0.5 ${isToday ? 'bg-blue-100' : ''}`}
                    style={{ width: `${pixelsPerDay}px` }}
                  >
                    <span className={`text-xs leading-none ${isToday ? 'font-bold text-blue-600' : 'text-muted-foreground'}`}>
                      {format(day, 'd')}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Resource rows */}
          {filteredResources?.map((resource) => (
            <React.Fragment key={resource?.id}>
              <div className="flex border-b border-border hover:bg-muted/20 transition-colors group">
                {/* Left columns */}
                <div className="flex flex-shrink-0 sticky left-0 z-10 bg-card group-hover:bg-muted/20 transition-colors" style={{ width: totalLeftWidth }}>
                  {ALL_COLUMNS?.filter(c => visibleCols?.includes(c?.id))?.map(col => (
                    <div
                      key={col?.id}
                      className="flex items-center px-2 py-1.5 border-r border-border overflow-hidden"
                      style={{ width: widths?.[col?.id] }}
                    >
                      {col?.id === 'name' && (
                        <button
                          onClick={() => handleResourceClick(resource)}
                          onDoubleClick={() => handleResourceDoubleClick(resource)}
                          className="flex items-center gap-1.5 text-left w-full min-w-0 group/name"
                          title="Click to view assignments · Double-click for details"
                        >
                          <Icon
                            name={expandedResource?.id === resource?.id ? 'ChevronDown' : 'ChevronRight'}
                            size={12}
                            className="flex-shrink-0 text-muted-foreground group-hover/name:text-accent transition-colors"
                          />
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-foreground truncate group-hover/name:text-accent transition-colors">
                              {resource?.name}
                            </p>
                          </div>
                          {resource?.conflicts?.length > 0 && (
                            <span className="flex-shrink-0 ml-auto inline-flex items-center gap-0.5 px-1 py-0.5 rounded-full bg-red-100 text-red-600 text-xs font-medium">
                              <Icon name="AlertTriangle" size={9} />
                              {resource?.conflicts?.length}
                            </span>
                          )}
                        </button>
                      )}
                      {col?.id === 'category' && (
                        <span className="text-xs text-muted-foreground truncate">{resource?.category || '—'}</span>
                      )}
                      {col?.id === 'status' && (
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium truncate ${STATUS_COLORS?.[resource?.status] || 'bg-gray-100 text-gray-600'}`}>
                          {resource?.status || '—'}
                        </span>
                      )}
                      {col?.id === 'assignments' && (
                        <span className="text-xs text-muted-foreground">{resource?.assignments?.length || 0}</span>
                      )}
                    </div>
                  ))}
                </div>

                {/* Timeline */}
                <div
                  className="flex-1 relative"
                  style={{ height: '40px', width: `${days?.length * pixelsPerDay}px` }}
                >
                  {days?.map((day, idx) => {
                    const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                    return (
                      <div
                        key={idx}
                        className={`absolute top-0 bottom-0 border-r border-border/20 ${isToday ? 'bg-blue-50' : ''}`}
                        style={{ left: `${idx * pixelsPerDay}px`, width: `${pixelsPerDay}px` }}
                      />
                    );
                  })}
                  {(resource?.assignments || [])?.map((assignment, index) => {
                    const barStyle = getBarStyle(assignment);
                    if (!barStyle) return null;
                    return (
                      <div
                        key={assignment?.id || index}
                        className={`absolute h-6 rounded flex items-center px-1.5 text-xs font-medium shadow-sm cursor-default ${getBarColor(assignment)}`}
                        style={{ ...barStyle, top: '7px' }}
                        title={`${assignment?.projectName || 'Project'}${assignment?.phaseName ? ` — ${assignment?.phaseName}` : ''}\n${assignment?.startDate ? format(new Date(assignment.startDate), 'MMM d, yyyy') : ''} – ${assignment?.endDate ? format(new Date(assignment.endDate), 'MMM d, yyyy') : ''}`}
                      >
                        <span className="truncate">{assignment?.projectName || 'Project'}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Expanded assignments panel */}
              {expandedResource?.id === resource?.id && (
                <AssignmentsPanel
                  resource={resource}
                  onClose={() => setExpandedResource(null)}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
      {/* Resource Detail Modal */}
      {detailResource && (
        <ResourceDetailModal
          resource={detailResource}
          onClose={() => setDetailResource(null)}
          onEdit={() => setDetailResource(null)}
          onDelete={() => setDetailResource(null)}
          onUpdateStatus={() => {}}
        />
      )}
    </div>
  );
};

export default ResourceGanttView;